// Composer Studio — 표 셀 키보드 내비게이션 공용 순수 함수.
//
// 엑셀식 셀 이동(Tab=다음/이전 셀, 화살표=인접 셀)이 병합 셀을 올바로 건너뛰도록,
// "실제 존재하는 셀"(시작 좌표에만 존재) 기준의 이동 계산을 순수 함수로 분리한다.
// 병합으로 덮인 슬롯은 cells에 없으므로 findCellAt이 undefined를 반환해 자연히 건너뛴다.

import type { TableNode } from '@safetyhub/shared';
import type { CellRef } from './computeCellRange';

/**
 * (row,col) 좌표에서 *시작하는* 실제 셀을 찾는다(없으면 undefined).
 * 병합으로 덮인 슬롯(다른 셀의 span 영역)은 cells에 없으므로 undefined.
 */
export function findCellAt(table: TableNode, row: number, col: number) {
  return table.cells.find((c) => c.row === row && c.col === col);
}

/**
 * Tab 내비게이션 — 현재 셀의 다음/이전 실제 셀(CellRef)을 구한다(병합 인지).
 *
 * - next: 같은 행에서 col+1부터 끝까지 실제 셀 탐색 → 없으면 다음 행의 첫 실제 셀(좌→우) → ... 표 끝이면 null.
 * - prev: 같은 행에서 col-1부터 0까지 실제 셀 탐색 → 없으면 이전 행의 마지막 실제 셀(우→좌) → ... 표 시작이면 null.
 * 병합 셀은 시작 좌표에만 존재하므로 덮인 슬롯은 findCellAt에서 자연히 건너뛴다.
 */
export function nextTableCell(
  table: TableNode,
  ref: CellRef,
  dir: 'next' | 'prev',
): CellRef | null {
  const { rowCount, colCount } = table;
  if (dir === 'next') {
    // 현재 행의 나머지 칸(col+1..colCount-1) 좌→우.
    for (let c = ref.col + 1; c < colCount; c++) {
      if (findCellAt(table, ref.row, c)) return { tableId: table.id, row: ref.row, col: c };
    }
    // 다음 행들의 첫 실제 셀(좌→우).
    for (let r = ref.row + 1; r < rowCount; r++) {
      for (let c = 0; c < colCount; c++) {
        if (findCellAt(table, r, c)) return { tableId: table.id, row: r, col: c };
      }
    }
    return null;
  }
  // prev — 현재 행의 앞 칸(col-1..0) 우→좌.
  for (let c = ref.col - 1; c >= 0; c--) {
    if (findCellAt(table, ref.row, c)) return { tableId: table.id, row: ref.row, col: c };
  }
  // 이전 행들의 마지막 실제 셀(우→좌).
  for (let r = ref.row - 1; r >= 0; r--) {
    for (let c = colCount - 1; c >= 0; c--) {
      if (findCellAt(table, r, c)) return { tableId: table.id, row: r, col: c };
    }
  }
  return null;
}

/**
 * 화살표 내비게이션 — 현재 셀에서 방향(상/하/좌/우)으로 가장 가까운 실제 셀(CellRef)을 구한다.
 *
 * 좌표를 1씩 이동하며 실제 셀이 나올 때까지(병합으로 덮인 슬롯은 건너뜀) 탐색하고,
 * 표 경계를 벗어나면 null(현 위치 유지). 행 이동(up/down)은 col 고정, 열 이동(left/right)은 row 고정.
 */
export function adjacentTableCell(
  table: TableNode,
  ref: CellRef,
  dir: 'up' | 'down' | 'left' | 'right',
): CellRef | null {
  const { rowCount, colCount } = table;
  const dr = dir === 'up' ? -1 : dir === 'down' ? 1 : 0;
  const dc = dir === 'left' ? -1 : dir === 'right' ? 1 : 0;
  let r = ref.row + dr;
  let c = ref.col + dc;
  while (r >= 0 && r < rowCount && c >= 0 && c < colCount) {
    if (findCellAt(table, r, c)) return { tableId: table.id, row: r, col: c };
    r += dr;
    c += dc;
  }
  return null;
}
